import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './NotificationPanel.css';
import AssignmentIcon from '@mui/icons-material/Assignment';
import QuizIcon from '@mui/icons-material/Quiz';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GradingIcon from '@mui/icons-material/Grading';
import ReplayIcon from '@mui/icons-material/Replay';
import CampaignIcon from '@mui/icons-material/Campaign';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { Notification } from '../hooks/useNotifications';

interface NotificationPanelProps {
  notifications: Notification[];
  unreadCount: number;
  onMarkAsRead: (id: number) => void;
  onMarkAllAsRead: () => void;
  onDelete: (id: number) => void;
  onClose: () => void;
}

const ICON_MAP: Record<string, React.ReactElement> = {
  student_submission: <AssignmentIcon fontSize="small" />,
  quiz_submission: <QuizIcon fontSize="small" />,
  interactive_submission: <TouchAppIcon fontSize="small" />,
  class_comment: <ChatBubbleIcon fontSize="small" />,
  student_enrolled: <PersonAddIcon fontSize="small" />,
  diagnostic_completed: <AssessmentIcon fontSize="small" />,
  work_graded: <GradingIcon fontSize="small" />,
  work_returned: <ReplayIcon fontSize="small" />,
  class_post: <CampaignIcon fontSize="small" />,
};

function timeAgo(dateStr: string, t: (key: string, opts?: any) => string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('notifications.justNow', 'Just now');
  if (mins < 60) return t('notifications.minutesAgo', { count: mins, defaultValue: '{{count}}m ago' });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t('notifications.hoursAgo', { count: hrs, defaultValue: '{{count}}h ago' });
  const days = Math.floor(hrs / 24);
  return t('notifications.daysAgo', { count: days, defaultValue: '{{count}}d ago' });
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  notifications,
  unreadCount,
  onMarkAsRead,
  onMarkAllAsRead,
  onDelete,
  onClose,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        const bell = document.querySelector('.notification-btn');
        if (bell && bell.contains(e.target as Node)) return;
        onClose();
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  const handleClick = (n: Notification) => {
    if (!n.is_read) onMarkAsRead(n.id);
    if (n.link) {
      navigate(n.link);
      onClose();
    }
  };

  return (
    <div
      className="notification-panel"
      ref={panelRef}
      role="dialog"
      aria-label={t('notifications.panelTitle', 'Notifications')}
    >
      <div className="notif-panel-header">
        <h3>{t('notifications.panelTitle', 'Notifications')}</h3>
        <div className="notif-panel-actions">
          {unreadCount > 0 && (
            <button
              className="notif-mark-all-btn"
              onClick={onMarkAllAsRead}
              aria-label={t('notifications.markAllRead', 'Mark all as read')}
              title={t('notifications.markAllRead', 'Mark all as read')}
            >
              <DoneAllIcon fontSize="small" />
            </button>
          )}
          <button
            className="notif-close-btn"
            onClick={onClose}
            aria-label={t('notifications.close', 'Close notifications')}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
      </div>

      <div className="notif-panel-list" role="list" aria-label={t('notifications.list', 'Notification list')}>
        {notifications.length === 0 ? (
          <div className="notif-empty">
            <NotificationsNoneIcon sx={{ fontSize: 40, color: '#b0b0b0' }} aria-hidden="true" />
            <p>{t('notifications.empty', 'No notifications yet')}</p>
          </div>
        ) : (
          notifications.map((n) => (
            <div
              key={n.id}
              className={`notif-item ${n.is_read ? 'read' : 'unread'}`}
              role="listitem"
              onClick={() => handleClick(n)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleClick(n); }}
              tabIndex={0}
              aria-label={`${n.is_read ? '' : t('notifications.unread', 'Unread') + ': '}${n.title}`}
            >
              <span className="notif-icon" aria-hidden="true">
                {ICON_MAP[n.notification_type] || <AssignmentIcon fontSize="small" />}
              </span>
              <div className="notif-content">
                <span className="notif-title">{n.title}</span>
                {n.message && <span className="notif-message">{n.message}</span>}
                <span className="notif-time">{timeAgo(n.created_at, t)}</span>
              </div>
              {!n.is_read && <span className="notif-unread-dot" aria-hidden="true" />}
              <button
                className="notif-delete-btn"
                onClick={(e) => { e.stopPropagation(); onDelete(n.id); }}
                aria-label={t('notifications.delete', 'Delete notification')}
                title={t('notifications.delete', 'Delete notification')}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default NotificationPanel;
